import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiPlus } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import Button from '../../../../components/Button';

interface AddContentButtonsProps {
  sessionId: string;
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

/** "Add Summary" / "Add Notes" seeds plus the "Add Unallocated Runs" navigation button. */
export default function AddContentButtons({
  sessionId,
  session,
  updateSession,
  invalidateSession,
}: AddContentButtonsProps): ReactElement {
  const navigate = useNavigate();

  const addTextField = async (field: 'summary' | 'notes') => {
    try {
      await updateSession.mutateAsync({ [field]: 'Text here...' });
      await invalidateSession();
      toast.success(field === 'summary' ? 'Summary added' : 'Notes added');
    } catch (e) {
      console.error(e);
      toast.error(field === 'summary' ? 'Failed to add summary' : 'Failed to add notes');
    }
  };

  return (
    <div className="cr-mt-4 cr-flex cr-flex-wrap cr-items-center cr-gap-3">
      {/* Add Summary button - only show if summary is null or empty */}
      {(!session?.summary || session.summary.length === 0) && (
        <Button
          onClick={() => addTextField('summary')}
          icon={<HiPlus className="cr-h-5 cr-w-5" />}
          size="compact"
          normalCase
        >
          Add Summary
        </Button>
      )}

      {/* Add Notes button - only show if notes is null or empty */}
      {(!session?.notes || session.notes.length === 0) && (
        <Button
          onClick={() => addTextField('notes')}
          icon={<HiPlus className="cr-h-5 cr-w-5" />}
          size="compact"
          normalCase
        >
          Add Notes
        </Button>
      )}

      {/* Add Unallocated Runs */}
      <Button
        onClick={() => {
          const targetConfigId = session?.run_order?.[0] || session?.configs?.[0]?.id;
          navigate('/runs', {
            state: {
              moveToSessionId: sessionId,
              moveToConfigId: targetConfigId,
              moveToSessionDesc: session?.description || 'Session',
            },
          });
        }}
        disabled={!session?.run_order?.[0] && !session?.configs?.[0]?.id}
        icon={<HiPlus className="cr-h-5 cr-w-5" />}
        size="compact"
        normalCase
      >
        Add Unallocated Runs
      </Button>
    </div>
  );
}
